import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ScrollableCurriculumTabStrip } from "@/components/ui/scrollable-curriculum-tab-strip";
import {
  Tabs,
  TabsContent,
  TabsTrigger,
} from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import type { BeltRank } from "@/types/belts";
import { useEffect, useMemo, useState } from "react";

interface OneStepProps {
  selectedBelt: BeltRank;
  getBeltColor: (belt: BeltRank) => string;
  requirements: string;
  /**
   * `curriculum` = requirements summary + numbered sub-tabs (main curriculum tab).
   * `legacy` (default) = original accordion list (dropdown).
   */
  presentation?: "legacy" | "curriculum";
  /** Belt-colored pills for curriculum step sub-nav only. */
  beltTabSurfaceClass?: string;
}

interface OneStepTechnique {
  number: number;
  description: string;
}

const DEFAULT_TAB_SURFACE = "!bg-gray-200 !text-gray-900";

const ALL_STEPS: OneStepTechnique[] = [
  { number: 1, description: "Step R foot at 45° into horse stance; simultaneous L knife-hand block and R high section punch." },
  { number: 2, description: "Step R foot forward into back stance, R inner hammer block, grab attackers R hand with your L, adjust to front stance R back fist strike to temple" },
  { number: 3, description: "Step R foot forward into R front stance; simultaneous L knife-hand block and right knife-hand strike to neck." },
  { number: 4, description: "Step L foot at 45° into horse stance; simultaneous R knife-hand block and L mid-section punch to ribs." },
  { number: 5, description: "Step R foot back into a L back stance; R front kick into 45° horse stance; simultaneous L knife-hand block and R high section punch." },

  { number: 6, description: "Step R foot 45° into horse stance; L knife-hand block with R high punch, L mid punch" },
  { number: 7, description: "Step R foot forward into R back stance; R Inner hammer block (grabbing attacker's hand with L hand), right back fist to temple, R elbow to ribs (in horse stance)." },
  { number: 8, description: "Step R foot forward into R front stance; simultaneous L knife-hand block and R knife hand strike to neck; grab attacker's shoulders and pull forward, L knee to solar plexus." },
  { number: 9, description: "Step L foot 45° into front stance; R outward knife-hand block, grab/twist attacker's wrist, pulling into R side kick to ribs." },
  { number: 10, description: "Step L foot at 45° into horse stance; R knife-hand block, L-R double mid-section punch, R round kick to solar plexus." },

  { number: 11, description: "Step R foot at 45° into horse stance; simultaneous L knife-hand block and R high section punch, L mid-section punch, R high section punch." },
  { number: 12, description: "Step R foot forward into R back stance, R Inner hammer block (grabbing attacker's hand with L hand), R backfist to temple. Step R foot at 45° into R front stance, L high section punch, L round kick to ribs." },
  { number: 13, description: "Step R foot forward into R front stance; simultaneous L knife-hand block and R knife hand strike to neck. Grab attacker's shoulders, inside sweep with L leg. Step over attacker to your L, R downward punch to face, R knee to the groin." },
  { number: 14, description: "Step R foot back into a L back stance, R high round kick, step down into R front stance. R palm block, L high section punch." },
  { number: 15, description: "Step L foot back into a R back stance, R sliding round kick, step down into R front stance. L knife-hand block, R high section punch." },

  { number: 16, description: "Step R foot at 45° into horse stance; L knife-hand block, R high section punch. R knife hand \"sticky block,\" pulling downward and to the R, L high section punch." },
  { number: 17, description: "Step R foot into R back stance, R Inner hammer block, R back fist to temple. Pivot anti-clockwise on balls of both feet into L cross-stance, L back fist. Continue around into L back stance, then step in with L foot to horse stance, L elbow to ribs." },
  { number: 18, description: "Step R foot into R front stance; simultaneous L knife-hand block and R knife hand strike to neck. Grab attacker's shoulders, outside sweep with R leg, R downward punch to face." },
  { number: 19, description: "Step L foot at 45° into horse stance; R knife-hand block, L R double mid-section punch, R round kick to solar plexus. Without putting R foot down, reach R hand under attacker's arm, grab shoulder, and R outside sweep (pulling off balance). R downward punch to face." },
  { number: 20, description: "Step R foot back into a L back stance, R back kick, step down into R front stance. R palm block, L high section punch." },

  { number: 21, description: "Step L foot at 45° to horse stance, R hand knife hand \"sticky\" block, L mid-section punch, L palm strike to back of elbow while twisting arm. Step L foot into L front stance, pin attacker's arm against your L thigh, R punch to temple." },
  { number: 22, description: "Step L foot at 45° to horse stance, R knife-hand block, L mid-section punch. Shift to L front stance, L knife-hand block and R knife-hand strike to neck." },
  { number: 23, description: "Step R foot back into L back stance; R crescent kick to wrist and (without setting foot down) R side kick. R front stance, R palm block, L high section punch." },
  { number: 24, description: "Step R foot back into L back stance, L side kick. Simultaneous L knife-hand block and R knife hand strike to neck. Grab shoulders, pull forward and down, R knee to solar plexus." },
  { number: 25, description: "Step L foot back into R back stance, R sliding kick, R palm block, L high section punch." },

  { number: 26, description: "Step R foot forward into R back stance, R Inner hammer block. R front stance, L high section punch. Simultaneous L knife-hand block and R knife hand strike to neck." },
  { number: 27, description: "Step L foot back into a R back stance, L middle front kick, as you step down, turn clockwise into R cross-stance, R back fist to temple. Continue around to R front stance, R hand fake, L high section punch." },
  { number: 28, description: "Step R foot at 45° into horse stance, L knife-hand block, R high section punch. Step L foot across to R, R back kick, R hand fake, L high section punch." },
  { number: 29, description: "Step L foot back (45°) into R back stance, R knife hand \"sticky\" block, twist wrist clockwise and R side kick. In R tiger stance, L high section punch. With L arm across attacker's chest, push off balance while sweeping with L leg from outside. R downward punch to face." },
  { number: 30, description: "Step L foot back to a R back stance, R jump front kick with fake. R palm block, L high section punch." },
];

function getStepsForBelt(belt: BeltRank): OneStepTechnique[] {
  const ranges: { [key in BeltRank]: [number, number] } = {
    WHITE: [1, 5],
    YELLOW: [1, 5],
    ORANGE: [1, 10],
    BLUE: [1, 10],
    GREEN: [6, 15],
    PURPLE: [6, 15],
    BROWN: [11, 20],
    RED: [11, 20],
    NAVY: [16, 25],
    "BLACK/WHITE": [16, 25],
    "BLACK/D1": [21, 30],
    "BLACK/D2": [21, 30],
    "BLACK/D3": [21, 30],
    "BLACK/D4": [21, 30],
    "BLACK/D5": [21, 30],
  };

  const [start, end] = ranges[belt];
  return ALL_STEPS.filter((step) => step.number >= start && step.number <= end);
}

/**
 * Split step text on commas, semicolons, and periods into bullets.
 * Delimiters inside double-quoted phrases are kept (e.g. `"sticky block,"`).
 */
export function splitDescriptionIntoBullets(description: string): string[] {
  const segments: string[] = [];
  let buf = "";
  let i = 0;
  while (i < description.length) {
    const c = description[i];
    if (c === '"') {
      buf += c;
      i++;
      while (i < description.length && description[i] !== '"') {
        buf += description[i];
        i++;
      }
      if (i < description.length) {
        buf += description[i];
        i++;
      }
      continue;
    }
    if (c === "," || c === ";" || c === ".") {
      const t = buf.trim();
      if (t) segments.push(t);
      buf = "";
      i++;
      while (i < description.length && description[i] === " ") i++;
      continue;
    }
    buf += c;
    i++;
  }
  const last = buf.trim();
  if (last) segments.push(last);
  return segments;
}

function StepDescriptionBullets({ description }: { description: string }) {
  const bullets = useMemo(() => splitDescriptionIntoBullets(description), [description]);
  return (
    <ul className="list-inside list-disc space-y-2 text-gray-700">
      {bullets.map((line, i) => (
        <li key={i}>{line}</li>
      ))}
    </ul>
  );
}

function OneStepLegacyLayout({
  requirements,
  steps,
}: {
  requirements: string;
  steps: OneStepTechnique[];
}) {
  return (
    <div className="mt-2 rounded-lg bg-gray-50 p-4">
      <div className="space-y-4">
        <div>
          <p className="italic text-gray-700">Required steps: {requirements}</p>
        </div>
        <Accordion type="single" collapsible className="w-full space-y-2">
          {steps.map((step) => (
            <AccordionItem key={step.number} value={`step-${step.number}`}>
              <AccordionTrigger className="rounded-lg bg-white px-4 py-2 hover:bg-gray-100 hover:no-underline">
                Step {step.number}
              </AccordionTrigger>
              <AccordionContent className="px-0">
                <div className="mt-2 rounded-lg bg-white p-4">
                  <StepDescriptionBullets description={step.description} />
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </div>
  );
}

function OneStepCurriculumLayout({
  requirements,
  steps,
  beltTabSurfaceClass,
}: {
  requirements: string;
  steps: OneStepTechnique[];
  beltTabSurfaceClass: string;
}) {
  const [activeStep, setActiveStep] = useState(() => String(steps[0]?.number ?? "1"));

  useEffect(() => {
    setActiveStep(String(steps[0]?.number ?? "1"));
  }, [steps]);

  const surface = beltTabSurfaceClass;

  return (
    <div className="space-y-6 text-left">
      <section className="rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5">
        <h3 className="mb-2 font-semibold text-lg text-taekwondo-red">Required for this belt</h3>
        <p className="text-gray-800">{requirements}</p>
      </section>

      <Tabs value={activeStep} onValueChange={setActiveStep} className="w-full space-y-4">
        <ScrollableCurriculumTabStrip
          key={steps.map((s) => s.number).join(",")}
          ariaLabelPrefix="One-Step steps"
        >
          {steps.map((step) => (
            <TabsTrigger
              key={step.number}
              value={String(step.number)}
              className={cn(
                "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium tabular-nums border border-black/10 transition-shadow",
                surface,
                "data-[state=active]:z-10 data-[state=active]:shadow-md data-[state=active]:ring-2 data-[state=active]:ring-gray-900/40 data-[state=active]:ring-offset-2 data-[state=active]:ring-offset-gray-100",
              )}
            >
              {step.number}
            </TabsTrigger>
          ))}
        </ScrollableCurriculumTabStrip>
        {steps.map((step) => (
          <TabsContent
            key={step.number}
            value={String(step.number)}
            className="mt-0 focus-visible:outline-none"
          >
            <div className="mt-2 rounded-lg bg-gray-50 p-4 md:p-5">
              <h4 className="mb-3 font-semibold text-base text-taekwondo-red">
                Step {step.number}
              </h4>
              <StepDescriptionBullets description={step.description} />
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

export default function OneStep({
  selectedBelt,
  requirements,
  presentation = "legacy",
  beltTabSurfaceClass = DEFAULT_TAB_SURFACE,
}: OneStepProps) {
  const steps = useMemo(() => getStepsForBelt(selectedBelt), [selectedBelt]);

  if (requirements === "Not applicable") {
    return null;
  }

  if (presentation === "curriculum") {
    return (
      <OneStepCurriculumLayout
        requirements={requirements}
        steps={steps}
        beltTabSurfaceClass={beltTabSurfaceClass}
      />
    );
  }

  return <OneStepLegacyLayout requirements={requirements} steps={steps} />;
}
